import "./licence-form.css";
import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Auth, useAuth } from "../auth/AuthContext";
import { issueTextOrDefault, OperationError } from "../DAO";
import { formatIsoDate } from "../Format";
import { InstanceStore, useInstanceStore } from "../instance/InstanceContext";
import { Licence } from "./Licence";
import { addNewLicence, LicenceFormData, replaceExistingLicence, updateExistingLicence, revokeLicence } from "./LicenceDAO";
import { LicenceOptionsStore, useLicenceOptionsStore } from "./LicenceOptions";
import { validateInteger, validateIsoDate } from "../CustomValidators";

interface FormFields {
   instanceId: string;
   endDate: string;
   maxUsers: string;
   maxPatients: string;
   usageMode: 'fixed' | 'report' | null;
   programTypeIds: string[];
   moduleIds: string[];
}

interface Props {
   licence: Licence | null;
   action: 'view' | 'add' | 'update' | 'replace';
   onSave?: () => void;
}
export const LicenceForm = ({ licence, action = 'view', onSave }: Props): JSX.Element => {
   const auth: Auth = useAuth();
   const canEdit = useMemo<boolean>(() => !!auth?.user?.permissions?.licence?.canEdit, [auth]);
   const edit = ['add', 'update', 'replace'].indexOf(action) !== -1;
   if (edit && !canEdit) {
      return <div>Invalid state</div>;
   }

   const navigate = useNavigate();

   const { register, formState: { errors }, handleSubmit, setError } = useForm<FormFields>({
      defaultValues: {
         instanceId: (licence?.instanceId ?? "") + "",
         endDate: licence?.endDate ?? "",
         maxUsers: (licence?.maxActiveUsers ?? "") + "",
         maxPatients: (licence?.maxActivePatients ?? "") + "",
         usageMode: licence?.reportRequired == null ? null : licence.reportRequired ? 'report' : 'fixed',
         programTypeIds: (licence?.programTypeIds ?? []).map(v => v + ""),
         moduleIds: (licence?.moduleIds ?? []).map(v => v + "")
      }
   });

   const [saveErrors, setSaveErrors] = useState<string[]>([]);

   const instanceStore: InstanceStore = useInstanceStore();
   const licenceOptions: LicenceOptionsStore = useLicenceOptionsStore();

   const startDate = licence?.startDate ?? formatIsoDate(new Date());

   function submit({ instanceId, endDate, maxUsers, maxPatients, usageMode, programTypeIds, moduleIds }: FormFields): void {
      const formData: LicenceFormData = {
         instanceId: +instanceId,
         endDate,
         maxActiveUsers: +maxUsers,
         maxActivePatients: +maxPatients,
         reportRequired: usageMode === 'report',
         programTypeIds: programTypeIds.map(v => +v),
         moduleIds: moduleIds.map(v => +v)
      }
      setSaveErrors([]); // clear non-form errors
      const handleSuccess = () => {
         onSave && onSave();
         navigate("/licences", { replace: true });
      };
      const handleFail = (err: OperationError) => {
         const { issues } = err
         // Try to assign issues to the appropriate fields, setting the rest on 'saveErrors'
         setSaveErrors(issues ? issues.filter(i => {
            if (i.path) {
               switch (i.path) {
                  case "instanceId":
                  case "endDate":
                  case "programTypeIds":
                  case "moduleIds":
                     setError(i.path, { type: 'server', message: i.details }, { shouldFocus: true });
                     return false;
                  case "maxActiveUsers":
                     setError("maxUsers", { type: 'server', message: i.details }, { shouldFocus: true });
                     return false;
                  case "maxActivePatients":
                     setError("maxPatients", { type: 'server', message: i.details }, { shouldFocus: true });
                     return false;
                  case "reportRequired":
                     setError("usageMode", { type: 'server', message: i.details }, { shouldFocus: true });
                     return false;
               }
            }
            return true;
         }).map(issueTextOrDefault) : []);
      };
      switch (action) {
         case "add":
            addNewLicence(formData).then(handleSuccess).catch(handleFail);
            break;
         case "replace":
            licence && replaceExistingLicence(licence.id, formData).then(handleSuccess).catch(handleFail);
            break;
         case "update":
            licence && updateExistingLicence(licence.id, formData).then(handleSuccess).catch(handleFail);
            break;
      }
   }

   function revoke(): void {
      if (licence && confirm("Are you sure you want to revoke this licence?")) {
         setSaveErrors([]); // clear non-form errors
         revokeLicence(licence.id).then(() => {
            onSave && onSave();
            navigate("/licences", { replace: true });
         }).catch((err: OperationError) => setSaveErrors(err.issues.map(issueTextOrDefault)));
      }
   }

   const htmlId = (suffix?: string): string => {
      const prefix = "licence-form";
      return suffix ? prefix + "_" + suffix : prefix;
   }
   return (
      <article id={htmlId()} style={{ display: "inline-block" }}>
         <form onSubmit={handleSubmit(submit)}>
            <ul>
               <li>
                  <label htmlFor={htmlId("instance")}>Instance:</label>
                  {licence?.instanceId == null ? <>
                     <select id={htmlId("instance")}
                        {...register("instanceId", {
                           required: "Instance is required"
                        })} className={errors.instanceId ? "error" : ""}>
                        <option value=""></option>
                        {instanceStore.instances?.map(i =>
                           <option key={i.id} value={i.id}>{i.name}</option>)}
                     </select>
                     {errors.instanceId &&
                        <label htmlFor={htmlId("instance")} className="error">{errors.instanceId.message}</label>}
                  </> :
                     <span id={htmlId("instance")}>
                        {instanceStore.instances?.find(i => i.id === licence?.instanceId)?.name ?? "???"}
                     </span>
                  }
               </li>
               <li>
                  <label htmlFor={htmlId("start-date")}>Start Date:</label>
                  <span id={htmlId("start-date")}>{startDate}</span>
               </li>
               <li>
                  <label htmlFor={htmlId("end-date")}>End Date:</label>
                  {edit ? <>
                     <input type="date" id={htmlId("end-date")} min={startDate}
                        {...register("endDate", {
                           required: "End Date is required",
                           validate: {
                              validDate: validateIsoDate
                           },
                           min: startDate
                        })}
                        className={errors.endDate ? "error" : ""} />
                     {errors.endDate &&
                        <label htmlFor={htmlId("endDate")} className="error">{errors.endDate.message}</label>}
                  </> :
                     <span id={htmlId("end-date")}>
                        {licence?.endDate ?? "???"}
                     </span>}
               </li>
               <li>
                  <label htmlFor={htmlId("max-users")}>Max Active Users:</label>
                  {edit ? <>
                     <input type="number" id={htmlId("max-users")} min={0}
                        {...register("maxUsers", {
                           required: "Max Active Users is required",
                           validate: {
                              validInt: validateInteger
                           },
                           max: {
                              value: 9999999,
                              message: "Enter a value less than or equal to 9999999."
                           },
                           min: {
                              value: 1,
                              message: "Enter a value greater than or equal to 1."
                           }
                        })}
                        className={errors.maxUsers ? "error" : ""} />
                     {errors.maxUsers &&
                        <label htmlFor={htmlId("max-users")} className="error">{errors.maxUsers.message}</label>}
                  </> :
                     <span id={htmlId("max-users")}>
                        {licence?.maxActiveUsers ?? "???"}
                     </span>}
               </li>
               <li>
                  <label htmlFor={htmlId("max-patients")}>Max Active Patients:</label>
                  {edit ? <>
                     <input type="number" id={htmlId("max-patients")} min={0}
                        {...register("maxPatients", {
                           required: "Max Active Patients is required",
                           validate: {
                              validInt: validateInteger
                           },
                           max: {
                              value: 9999999,
                              message: "Enter a value less than or equal to 9999999."
                           },
                           min: {
                              value: 1,
                              message: "Enter a value greater than or equal to 1."
                           }
                        })}
                        className={errors.maxPatients ? "error" : ""} />
                     {errors.maxPatients &&
                        <label htmlFor={htmlId("max-patients")} className="error">{errors.maxPatients.message}</label>}
                  </> :
                     <span id={htmlId("max-patients")}>
                        {licence?.maxActivePatients ?? "???"}
                     </span>}
               </li>
               <li>
                  <label>Licence Mode:</label>
                  {edit ? <>
                     <ol>
                        <li>
                           <input id={htmlId("usage-fixed")} type="radio" value="fixed"
                              {...register("usageMode", {
                                 required: "Licence Mode is required"
                              })} />
                           <label htmlFor={htmlId("usage-fixed")}>Fixed Limit</label>
                        </li>
                        <li>
                           <input id={htmlId("usage-report")} type="radio" value="report"
                              {...register("usageMode")} />
                           <label htmlFor={htmlId("usage-report")}>Report Usage</label>
                        </li>
                     </ol>
                     {errors.usageMode &&
                        <label className="error">{errors.usageMode.message}</label>}
                  </> :
                     <span id={htmlId("usage")}>
                        {licence?.reportRequired ? 'report' : 'fixed'}
                     </span>}
               </li>
               <li>
                  <fieldset>
                     <legend>Program Types:</legend>
                     <ol>
                        {edit ? licenceOptions.programTypes?.map(pt => <li key={pt.id}>
                           <input id={htmlId(`prog-type-${pt.id}`)} type="checkbox" value={pt.id + ""}
                              {...register("programTypeIds")} />
                           <label htmlFor={htmlId(`prog-type-${pt.id}`)}>{pt.code} &mdash; {pt.name}</label>
                        </li>)
                           : licence?.programTypeIds?.length ? licenceOptions.programTypes?.filter(pt => {
                              return licence?.programTypeIds.indexOf(pt.id) !== -1;
                           }).map(pt => <li key={pt.id} data-value={pt.id}>{pt.code} &mdash; {pt.name}</li>)
                              : <li>{"<none>"}</li>}
                     </ol>
                     {errors.programTypeIds &&
                        <label className="error">{errors.programTypeIds.message}</label>}
                  </fieldset>
                  <fieldset>
                     <legend>Modules:</legend>
                     <ol>
                        {edit ? licenceOptions.modules?.map(mod => <li key={mod.id}>
                           <input id={htmlId(`mod-${mod.id}`)} type="checkbox" value={mod.id + ""}
                              {...register("moduleIds")} />
                           <label htmlFor={htmlId(`mod-${mod.id}`)}>{mod.code}</label>
                        </li>) : licence?.moduleIds?.length ? licenceOptions.modules?.filter(mod => {
                           return licence?.moduleIds.indexOf(mod.id) !== -1;
                        }).map(mod => <li key={mod.id} data-value={mod.id}>{mod.code}</li>)
                           : <li>{"<none>"}</li>}
                     </ol>
                     {errors.moduleIds &&
                        <label className="error">{errors.moduleIds.message}</label>}
                  </fieldset>
               </li>
               <li id={htmlId("action-buttons")}><>
                  <span>
                     {edit ? <>
                        <button id={htmlId("save")} type="submit">Save</button>
                        <button id={htmlId("cancel")} type="button" onClick={() => {
                           navigate("..", { replace: true });
                        }}>Cancel</button>
                     </>
                        :
                        <button id={htmlId("okay")} type="button" onClick={() => {
                           navigate("/licences", { replace: true });
                        }}>Okay</button>}
                  </span>

                  {saveErrors.length !== 0 && <span>
                     {saveErrors.map(msg => <div><label className="error">{msg}</label></div>)}
                  </span>}

                  {canEdit && !edit && licence && <span>
                     {!licence.readOnly && <>
                        {licence.lastSync ?
                           <button id={htmlId("replace")} type="button"
                              title={"Create a new licence defaulting to values from this one.\nMark this one revoked."}
                              onClick={() => {
                                 navigate(`/licences/${licence.id}/replace`);
                              }}>Replace</button>
                           :
                           <button id={htmlId("update")} type="button"
                              title="Update this licence before it is synchronized."
                              onClick={() => {
                                 navigate(`/licences/${licence.id}/update`);
                              }}>Update</button>
                        }
                        <button id={htmlId("revoke")} type="button"
                           title={"Revoke this licence.\nIt will no longer be used on the client side after synchronized."}
                           onClick={revoke}>Revoke</button>
                     </>}
                     <button id={htmlId("clone")} type="button"
                        title="Create a new licence defaulting to values from this one."
                        onClick={() => {
                           navigate(`/licences/${licence.id}/clone`);
                        }}>Clone</button>
                  </span>}
               </></li>
            </ul>
         </form>
      </article>
   );
};